import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  InputBase,
  Divider,
  List,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemButton,
  Popover,
  CircularProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  IconBorderAll,
  IconBorderNone,
  IconCalendar,
  IconCalendarOff,
  IconCheck,
  IconCloudUpload,
  IconCopy,
  IconDownload,
  IconFileCheck,
  IconInfoCircle,
  IconMail,
  IconMaximize,
  IconMinimize,
  IconUpload,
} from "@tabler/icons";
import { GetChat, GetChatMessages, UploadFileMessage } from "./actions";
import { useDispatch } from "react-redux";
import { useSocket } from "src/providers/SocketProvider";
import {
  CheckTypeFile,
  dataLocalStorage,
  getLocalStorage,
} from "src/helper/publicFunction";
import UrlApi from "src/utils/Url";
import { toast } from "react-toastify";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@mui/material/styles";

export default function Support() {
  const theme = useTheme();
  let dataPrivileges = getLocalStorage(dataLocalStorage.privileges);
  const { socket } = useSocket();
  const dispatch = useDispatch();
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatThreads, setChatThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [offset, setOffset] = useState(0);
  const dataUser = getLocalStorage(dataLocalStorage.userinfo);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState({});
  const [v, setv] = useState("outlined");
  const [d, setd] = useState(false);

  const downloadFile = async ({ downloadLink, type }) => {
    try {
      // alert(type);
      fetch(downloadLink, {
        method: "GET",
      })
        .then((response) => response.blob())
        .then((blob) => {
          // Create blob link to download
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `FileName.${type}`);

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          link.parentNode.removeChild(link);
        });
    } catch (error) {
      toast.error("Failed to download, try again");
      console.log({ error });
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInfoClick = (event, messageId) => {
    setPopoverAnchorEl((prevState) => ({
      ...prevState,
      [messageId]: event.currentTarget,
    }));
  };

  const handleClose = (messageId) => {
    setPopoverAnchorEl((prevState) => {
      const newState = { ...prevState };
      delete newState[messageId];
      return newState;
    });
  };

  const getMessages = (chatId, offsetVal) => {
    setLoadingMessages(true);
    dispatch(
      GetChatMessages({
        offset: offsetVal,
        chat_id: chatId,
        callback: (data) => {
          setLoadingMessages(false);
          if (data.data) {
            setMessages((prev) => {
              // Avoid duplication
              const newIds = new Set(prev.map((msg) => msg.id));
              const filtered = data.data.filter((msg) => !newIds.has(msg.id));
              return [...prev, ...filtered];
            });
            setOffset((prev) => prev + 10);
          }
        },
      })
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      if (scrollTop === 0 && !loadingMessages && selectedChatId) {
        getMessages(selectedChatId, offset);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedChatId, offset, loadingMessages]);

  useEffect(() => {
    console.log("Selected chat changed:", selectedChatId);
    console.log("Offset reset:", offset);
    console.log("Messages cleared:", messages);
  }, [selectedChatId]);

  const user_id = dataUser.id;
  const name = dataUser.fullname;
  const main_img = dataUser.main_img;

  // API -> GET CHATS
  useEffect(() => {
    setLoadingChats(true);
    dispatch(
      GetChat({
        callback: (data) => {
          setLoadingChats(false);
          console.log(data);
          if (data.data) {
            setChatThreads(data?.data);
          }
        },
      })
    );
  }, []);

  // SOCKET -> NEW MESSAGE
  useEffect(() => {
    if (!socket) return;
    if (!selectedChatId) return;

    const handleReceiveMessage = (message) => {
      console.log("Received message:", message);
      console.log("message.id_chat", message.id_chat);
      console.log("selectedChatId", selectedChatId);
      if (message.id_chat !== selectedChatId) {
        return;
      }
      setMessages((prev) => [message, ...prev]);
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    };

    socket.on("receive_message", handleReceiveMessage);

    return () => {
      socket.off("receive_message", handleReceiveMessage);
    };
  }, [socket, selectedChatId]);

  // SOCKET -> NEW CHAT
  useEffect(() => {
    if (!socket) return;

    const handleReceiveChat = (chat) => {
      console.log("Received chat:", chat);

      const newChat = {
        id: chat?.id,
        image: chat?.image ?? "",
        name: chat?.name,
        last_message: chat?.last_message,
        last_message_type: chat?.last_message_type,
        date: Date.now(),
        sender_id: chat?.sender_id,
        sender_is_admin: 0,
      };

      setChatThreads((prev) => [newChat, ...prev]);
    };

    socket.on("receivechat", handleReceiveChat);

    return () => {
      socket.off("receivechat", handleReceiveChat);
    };
  }, [socket]);

  // SOCKET + API -> SEND MESSAGE
  const handleSend = () => {
    if (input.trim()) {
      const newMessage = {
        message: input,
        message_type: "text",
        id_chat: selectedChatId,
      };

      socket.emit("send_message", newMessage);

      const newMessageUI = {
        status_message: "isSent",
        image: main_img,
        name: name ?? "",
        message: input,
        id_chat: selectedChatId,
        sender_id: user_id,
        message_type: "text",
        sender_is_admin: 1,
        date: Date.now(),
      };

      setMessages((prev) => [newMessageUI, ...prev]);

      setInput("");
      setFile(null);

      setTimeout(() => {
        scrollToBottom();
      }, 500);
    } else if (file) {
      let fileType = "image";
      const fileTypeCheckerResult = CheckTypeFile({ name: file.name });
      if (fileTypeCheckerResult === "unknown") {
        fileType = "file";
      }
      dispatch(
        UploadFileMessage({
          file: file,
          callback: (data) => {
            if (data?.data) {
              const fileName = data?.data;
              const newMessage = {
                message: fileName,
                message_type: fileType,
                id_chat: selectedChatId,
              };
              socket.emit("send_message", newMessage);

              const newMessageUI = {
                status_message: "isSent",
                image: main_img,
                name: name ?? "",
                message: fileName,
                id_chat: selectedChatId,
                sender_id: user_id,
                message_type: fileType,
                sender_is_admin: 1,
                date: Date.now(),
              };

              setMessages((prev) => [newMessageUI, ...prev]);

              setInput("");
              setFile(null);
            }
          },
        })
      );
    }
  };

  // API -> GET MESSAGES
  useEffect(() => {
    if (selectedChatId) {
      setMessages([]);
      setOffset(0);
      setLoadingMessages(true);
      dispatch(
        GetChatMessages({
          chat_id: selectedChatId,
          offset: 0,
          callback: (data) => {
            setLoadingMessages(false);
            if (data.data) {
              setMessages(data.data);
              setOffset(10);
            }
          },
        })
      );
    }
  }, [selectedChatId]);

  // SOCKET -> UPDATE CHAT LIST
  const updateChatList = (chatThreads, update_chat, selectedChatId) => {
    const index = chatThreads.findIndex((chat) => chat.id === update_chat.id);
    const isUnread = update_chat.id !== selectedChatId;

    if (index !== -1) {
      // Update existing
      const updated = [...chatThreads];
      updated[index] = {
        ...updated[index],
        ...update_chat,
        hasNewMessage: isUnread,
      };
      return updated;
    } else {
      // Add new
      return [{ ...update_chat, hasNewMessage: isUnread }, ...chatThreads];
    }
  };

  useEffect(() => {
    if (!socket) return;
    const handleChatUpdate = async (update_chat) => {
      console.log("update_chat", update_chat);
      if (selectedChatId !== update_chat?.id) {
        toast.info(
          <>
            <strong>{update_chat.name}</strong>
            <div>{update_chat.last_message}</div>
            <small style={{ fontSize: "0.75rem", color: "#999" }}>
              {moment(update_chat.date).calendar()}
            </small>
          </>,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
      try {
        // setChatThreads((prev) => updateChatList(prev, update_chat));
        setChatThreads((prev) =>
          updateChatList(prev, update_chat, selectedChatId)
        );
      } catch (error) {
        console.log({ error });
      }
    };

    socket.on("update_chat", handleChatUpdate);
    return () => {
      socket.off("update_chat", handleChatUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    setChatThreads((prev) =>
      prev.map((chat) =>
        chat.id === selectedChatId ? { ...chat, hasNewMessage: false } : chat
      )
    );
  }, [selectedChatId]);

  const [copyIndex, setCopyIndex] = useState(null);

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: isExpanded ? "75vh" : "70vh",
        width: isExpanded ? "100%" : "100%",
        maxWidth: isExpanded ? "none" : "1200px",
        mx: "auto",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Sidebar */}
      <Paper
        variant={v}
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          mt: 4,
          p: 0,
          borderRadius: 2,
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: "250px",
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box borderRadius={0} p={2} borderBottom={1} borderColor="divider">
            <Typography variant="h6">
              Chats ({chatThreads?.length ?? 0})
            </Typography>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              borderRadius: 0,
              scrollbarColor: "#c3cfe2 #e5ebf3", //thumb color, then track color
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e5ebf3",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c3cfe2",
                borderRadius: "4px",
                transition: "background-color 0.2s ease-in-out",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#c3cfe2", // Darker for clearer hover visibility
              },
            }}
          >
            <List dense>
              {loadingChats === true && chatThreads.length === 0 ? (
                <Box
                  display={"flex"}
                  width={"100%"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  mt={100}
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (
                chatThreads.map((chat, i) => {
                  const lastMessage = chat?.last_message;

                  return (
                    <ListItemButton
                      key={i}
                      selected={selectedChatId === chat.id}
                      onClick={() => {
                        setLoadingMessages(true);
                        setMessages([]); // clear messages for new chat
                        setOffset(0); // reset offset
                        dispatch(
                          GetChatMessages({
                            chat_id: chat.id,
                            offset: 0,
                            callback: (data) => {
                              setLoadingMessages(false);
                              if (data.data) {
                                setSelectedChatId(chat.id);
                                setMessages(data.data);
                                setOffset(1); // initial offset after first load
                                setTimeout(() => {
                                  scrollToBottom();
                                }, 500);
                              }
                            },
                          })
                        );
                      }}
                      sx={{ alignItems: "flex-start" }}
                    >
                      <ListItemAvatar>
                        <Avatar src={UrlApi.baseUrlImageClient + chat?.image} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            textTransform={"capitalize"}
                            sx={{ maxWidth: "180px", fontWeight: "bold" }}
                          >
                            {chat?.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "180px" }}
                          >
                            {lastMessage}
                          </Typography>
                        }
                      />
                      {chat?.hasNewMessage && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "red",
                            borderRadius: "50%",
                            ml: 1,
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
          }}
        >
          {/* Chat Header */}
          <Box
            p={1}
            borderBottom={1}
            borderColor="divider"
            bgcolor="primary.main"
            color="white"
            sx={{
              borderTopRightRadius: 10,
              borderTopLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              color={"white"}
              variant="h6"
              textTransform={"capitalize"}
            >
              {chatThreads.find((c) => c.id === selectedChatId)?.name}
            </Typography>

            <Box>
              <Tooltip
                placement="top"
                title={isExpanded ? "Minimize" : "Maximize"}
              >
                <IconButton
                  onClick={() => setIsExpanded((prev) => !prev)}
                  color="inherit"
                >
                  {isExpanded ? (
                    <IconMinimize size={20} />
                  ) : (
                    <IconMaximize size={20} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip
                placement="top"
                title={v === "outlined" ? "Shadow" : "Bordered"}
              >
                <IconButton
                  onClick={() => {
                    if (v === "outlined") {
                      setv("elevation");
                    } else {
                      setv("outlined");
                    }
                  }}
                  color="inherit"
                >
                  {v === "outlined" ? (
                    <IconBorderAll size={20} />
                  ) : (
                    <IconBorderNone size={20} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                placement="top"
                title={d === false ? "Show dates" : "Hide dates"}
              >
                <IconButton
                  onClick={() => {
                    if (d === true) {
                      setd(false);
                    } else {
                      setd(true);
                    }
                  }}
                  color="inherit"
                >
                  {d === false ? (
                    <IconCalendar size={20} />
                  ) : (
                    <IconCalendarOff size={20} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Messages */}
          <Box
            ref={containerRef}
            flexGrow={1}
            overflow="auto"
            p={2}
            display="flex"
            flexDirection="column"
            borderRadius={0}
            gap={2}
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(to bottom, #1a1a1a, #2c3e50)" // Dark mode gradient
                  : "linear-gradient(to bottom, rgb(255, 255, 255), #c3cfe2)", // Light mode gradient
              scrollbarColor: "#c3cfe2 #e5ebf3", //thumb color, then track color
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e5ebf3",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c3cfe2",
                borderRadius: "4px",
                transition: "background-color 0.2s ease-in-out",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#c3cfe2", // Darker for clearer hover visibility
              },
            }}
          >
            {loadingMessages === true && messages?.length == 0 ? (
              <Box
                display={"flex"}
                width={"100%"}
                justifyContent={"center"}
                alignItems={"center"}
                mt={"25vh"}
              >
                <CircularProgress size={20} />
              </Box>
            ) : loadingMessages === false && messages?.length == 0 ? (
              <>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 4,
                    color: "text.secondary",
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <IconMail sx={{ fontSize: 40, color: "grey.500" }} />
                    <Typography variant="h6">
                      Select a chat to get started
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose a conversation from the list to view messages.
                    </Typography>
                  </Stack>
                </Box>
              </>
            ) : (
              messages
                .slice()
                .reverse()
                .map((msg, i) => {
                  const open = Boolean(popoverAnchorEl[msg.id]);
                  const id = open ? `popover-${msg.id}` : undefined;

                  return (
                    <Box
                      key={i}
                      display="flex"
                      justifyContent={
                        msg.sender_is_admin === 1 ? "flex-end" : "flex-start"
                      }
                      position="relative"
                    >
                      <Box
                        sx={{
                          my: d ? 0.3 : undefined,
                          bgcolor:
                            msg?.message_type === "image"
                              ? "transparent"
                              : msg.sender_is_admin === 1
                              ? "primary.dark"
                              : "grey.300",
                          color:
                            msg.sender_is_admin === 1
                              ? "primary.contrastText"
                              : "text.primary",
                          p: 1.5,
                          borderRadius: 2,
                          maxWidth: "250px",
                          position: "relative",
                          "&:hover .info-btn": {
                            opacity: 1,
                          },
                        }}
                      >
                        {msg?.message_type === "image" ? (
                          <Box>
                            <img
                              src={UrlApi.baseUrlImageClient + msg?.message}
                              alt={msg?.message}
                              style={{
                                maxWidth: "200px",
                                width: "100%",
                                height: "auto",
                                borderRadius: "8px",
                              }}
                            />
                            {d && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  fontSize: 10,
                                  textAlign:
                                    msg.sender_is_admin === 1
                                      ? "right"
                                      : "left",
                                }}
                              >
                                {moment(msg?.date).format("DD/MM/YYYY HH:mm")}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontWeight: d ? "bold" : undefined,
                                textAlign:
                                  msg.sender_is_admin === 1 ? "right" : "left",
                              }}
                            >
                              {msg?.message}
                            </Typography>
                            {d && (
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  fontSize: 10,
                                  textAlign:
                                    msg.sender_is_admin === 1
                                      ? "right"
                                      : "left",
                                }}
                              >
                                {moment(msg?.date).format("DD/MM/YYYY HH:mm")}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Tooltip Icon shown only on hover */}

                        {msg?.message_type !== "text" && (
                          <Tooltip title={"Download"} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                console.log("link123", msg?.message);
                                downloadFile({
                                  downloadLink:
                                    UrlApi.baseUrlImageClient + msg?.message,
                                  type: msg?.message?.split(".")[1],
                                });
                              }}
                              className="info-btn"
                              sx={{
                                position: "absolute",
                                top: 4,
                                right:
                                  msg.sender_is_admin === 1 ? undefined : -30,
                                left:
                                  msg.sender_is_admin === 1 ? -30 : undefined,
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                color: "grey.700",
                              }}
                            >
                              <IconDownload size={20} />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title={"Info"} arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(event) => handleInfoClick(event, msg.id)}
                            className="info-btn"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right:
                                msg.sender_is_admin === 1
                                  ? undefined
                                  : msg?.message_type !== "text"
                                  ? -60
                                  : -30,
                              left:
                                msg.sender_is_admin === 1
                                  ? msg?.message_type !== "text"
                                    ? -60
                                    : -30
                                  : undefined,
                              opacity: 0,
                              transition: "opacity 0.2s ease-in-out",
                              color: "grey.700",
                            }}
                          >
                            <IconInfoCircle size={20} />
                          </IconButton>
                        </Tooltip>

                        {msg?.message_type === "text" && (
                          <Tooltip
                            title={copyIndex === i ? "Copied!" : "Copy"}
                            arrow
                            placement="top"
                          >
                            <motion.div
                              onClick={() => {
                                if (copyIndex === i) return;
                                setCopyIndex(i);
                                handleCopy(msg?.message);
                                setTimeout(() => setCopyIndex(-1), 2000); // reset after 2s
                              }}
                              initial={{ scale: 1 }}
                              animate={
                                copyIndex === i
                                  ? { scale: [1, 1.3, 1] }
                                  : { scale: 1 }
                              }
                              transition={{ duration: 0.4 }}
                              style={{
                                position: "absolute",
                                top: 4,
                                right:
                                  msg.sender_is_admin === 1 ? undefined : -60,
                                left:
                                  msg.sender_is_admin === 1 ? -60 : undefined,
                                transition: "opacity 0.2s ease-in-out",
                              }}
                            >
                              <IconButton
                                className="info-btn"
                                size="small"
                                sx={{
                                  color: "grey.700",
                                  opacity: 0,
                                  transition: "opacity 0.2s ease-in-out",
                                }}
                              >
                                <AnimatePresence mode="wait" initial={false}>
                                  {copyIndex === i ? (
                                    <motion.div
                                      key="check"
                                      initial={{ opacity: 0, rotate: -90 }}
                                      animate={{ opacity: 1, rotate: 0 }}
                                      exit={{ opacity: 0, rotate: 90 }}
                                      transition={{ duration: 0.25 }}
                                    >
                                      <IconCheck color="green" size={20} />
                                    </motion.div>
                                  ) : (
                                    <motion.div
                                      key="copy"
                                      initial={{ opacity: 0, scale: 0.5 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.5 }}
                                      transition={{ duration: 0.25 }}
                                    >
                                      <IconCopy size={20} />
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </IconButton>
                            </motion.div>
                          </Tooltip>
                        )}

                        {/* Popover to show info */}
                        <Popover
                          id={id}
                          open={open}
                          anchorEl={popoverAnchorEl[msg.id]}
                          onClose={() => handleClose(msg.id)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          <Box p={1.5} maxWidth="300px">
                            <Typography
                              textTransform={"capitalize"}
                              gutterBottom
                              variant="body2"
                            >
                              <strong>Sender:</strong> {msg.name}
                            </Typography>

                            <Typography gutterBottom variant="body2">
                              <strong>Status:</strong>{" "}
                              {msg.status_message === "isSent"
                                ? "Delivered"
                                : ""}
                            </Typography>

                            <Typography gutterBottom variant="body2">
                              <strong>Date:</strong>{" "}
                              {moment(msg?.date).format("YYYY-MM-DD HH:mm:ss")}
                            </Typography>
                          </Box>
                        </Popover>
                      </Box>
                    </Box>
                  );
                })
            )}

            <div ref={bottomRef} />
          </Box>

          <Divider />

          {/* Input Area */}
          {dataPrivileges["25"].access_write == 1 &&
          dataPrivileges["25"].access_edit == 1 ? (
            <Box p={1} display="flex" alignItems="center" gap={1}>
              {selectedChatId && (
                <Tooltip title={"Attach File or Image"}>
                  <IconButton component="label">
                    {!!file ? <IconFileCheck /> : <IconUpload />}
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        setInput("");
                        setFile(e.target.files?.[0]);
                      }}
                      accept="application/pdf,image/*"
                    />
                  </IconButton>
                </Tooltip>
              )}

              <InputBase
                disabled={!!file || !selectedChatId}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                fullWidth
                placeholder={
                  !selectedChatId
                    ? ""
                    : !file
                    ? "Type your message..."
                    : "File Selected ✅"
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                sx={{ ml: 1 }}
              />

              {selectedChatId && (
                <Tooltip title={!!file ? "Upload & Send File" : "Send Message"}>
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={!input && !file}
                  >
                    {!!file ? <IconCloudUpload /> : <SendIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
